// Flameco UI kit v2.8.0, copied by authservice ui-kit/sync.js. Do not edit; change the kit and re-sync.
// Light / dark mode that follows the person across Flameco apps.
// The choice is kept in a cookie on .flameco.com (every *.flameco.com app reads
// the same one; on localhost all ports share it too) and in localStorage as a
// fallback for addresses that can't share cookies (Legacy ERP on the LAN).
// Applied as <html data-theme="light|dark"> plus the `dark` class for Tailwind.

export type FlamecoTheme = 'light' | 'dark';

const KEY = 'fl_theme';
const listeners = new Set<(t: FlamecoTheme) => void>();
let watching = false;

function cookieDomain(): string {
  const h = window.location.hostname;
  return h === 'flameco.com' || h.endsWith('.flameco.com') ? '; Domain=.flameco.com' : '';
}

function read(): FlamecoTheme | null {
  try {
    const m = document.cookie.match(/(?:^|;\s*)fl_theme=(light|dark)/);
    if (m) return m[1] as FlamecoTheme;
  } catch {
    /* cookies blocked */
  }
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    /* storage blocked */
  }
  return null;
}

/** The person's choice; light unless they picked dark (the Flameco default). */
export function getTheme(): FlamecoTheme {
  return read() ?? 'light';
}

function apply(t: FlamecoTheme) {
  const el = document.documentElement;
  el.setAttribute('data-theme', t);
  el.classList.toggle('dark', t === 'dark');
  el.style.colorScheme = t;
}

/** Applies the saved theme. Call once at startup (the boot script below does it before paint). */
export function initTheme(): FlamecoTheme {
  const t = getTheme();
  apply(t);
  if (watching) return t; // the frame mounts often; listen once
  watching = true;
  // Another tab of this app changed it.
  window.addEventListener('storage', (e) => {
    if (e.key === KEY && (e.newValue === 'light' || e.newValue === 'dark')) {
      apply(e.newValue);
      listeners.forEach((fn) => fn(e.newValue as FlamecoTheme));
    }
  });
  // Changed in another Flameco app while this tab was in the background.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const now = getTheme();
    if (document.documentElement.getAttribute('data-theme') !== now) {
      apply(now);
      listeners.forEach((fn) => fn(now));
    }
  });
  return t;
}

export function setTheme(t: FlamecoTheme): void {
  try {
    document.cookie = `${KEY}=${t}; Path=/; Max-Age=31536000; SameSite=Lax${cookieDomain()}`;
  } catch {
    /* cookies blocked */
  }
  try {
    localStorage.setItem(KEY, t);
  } catch {
    /* storage blocked */
  }
  apply(t);
  listeners.forEach((fn) => fn(t));
}

export function onThemeChange(fn: (t: FlamecoTheme) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Inline this in <head> (before any CSS paints) so a dark-mode person never
 * sees a white flash: Next.js `<script dangerouslySetInnerHTML={{ __html: THEME_BOOT_SCRIPT }} />`,
 * CRA public/index.html as a plain <script>.
 */
export const THEME_BOOT_SCRIPT = `(function(){try{var m=document.cookie.match(/(?:^|;\\s*)fl_theme=(light|dark)/);var t=m?m[1]:localStorage.getItem('fl_theme');if(t!=='dark')t='light';var e=document.documentElement;e.setAttribute('data-theme',t);if(t==='dark')e.classList.add('dark');e.style.colorScheme=t;}catch(_){}})();`;
